<template>
  <Page>
    <PageContent>
      <v-form ref="formRef">
        <FormLayout
          :header-content="t('deepbox.settings.cloudoffice.description')"
        >
          <InputListItemSwitch
            v-model="isCloudOfficeActive"
            :subtitle="t('deepbox.settings.cloudoffice.status_switch.subtitle')"
            title="Microsoft Office 365"
          >
            <template #prepend>
              <v-avatar image="/logos/microsoft_365_logo.png" size="28" tile />
            </template>
            <template v-if="termsItemsSorted.length" #append-prepend>
              <CloudOfficeHistoryDialog :items="termsItemsSorted" />
            </template>
          </InputListItemSwitch>
        </FormLayout>
      </v-form>
    </PageContent>
  </Page>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import {
  CreateTermsOfUseDeclarationRequestStatus,
  GetTermsOfUseDeclarationResponse,
} from '@deepcloud/api-deep-admin-backend'
import { useLoader } from '@deepcloud/deep-ui-lib'
import { useParams } from '@deepcloud/deep-ui-utils/router'
import { useAdminUserContext } from '@/composables/deepadmin/use-admin-user-context.ts'
import { useAuthorizationContext } from '@/composables/deepadmin/use-authorization-context.ts'
import usePageAccess from '@/composables/deepadmin/use-page-access.ts'
import { useApi } from '@/composables/use-api.ts'
import CloudOfficeHistoryDialog from '@/components/deepbox/cloudoffice/CloudOfficeHistoryDialog.vue'
import FormLayout from '@/components/forms/FormLayout.vue'
import InputListItemSwitch from '@/components/forms/inputs/InputListItemSwitch.vue'
import Page from '@/components/page/Page.vue'
import PageContent from '@/components/page/PageContent.vue'

definePage({
  meta: {
    breadcrumbLabelKey: 'navigation.deepbox.settings',
  },
})

usePageAccess(computed(() => !permissions.canViewBoxes))

const { t } = useI18n()
const deepLoader = useLoader()
const { permissions } = useAuthorizationContext()
const { deepAdminBackendAPI } = useApi()

const params = useParams<{ organizationId: string }>()

const TERMS_OF_USE_TYPE = 'microsoft_cspp_wopi'

const termsItems = ref<GetTermsOfUseDeclarationResponse[]>([])

const termsItemsSorted = computed(() =>
  termsItems.value.slice().sort((x, y) => {
    if (!x?.createdAt || !y?.createdAt) return 0
    return new Date(y.createdAt).getTime() - new Date(x.createdAt).getTime()
  }),
)

const isCloudOfficeActive = computed({
  get: () => termsItemsSorted.value?.[0]?.status === 'accepted',
  set: (v) => {
    onCreate(v ? 'accepted' : 'declined')
  },
})

const { user } = useAdminUserContext()

async function onFetchAll() {
  try {
    deepLoader.show()
    const { data } = await deepAdminBackendAPI.value.termsOfUseDeclarations.get(
      {
        termsOfUseType: TERMS_OF_USE_TYPE,
        orgId: params.value.organizationId,
        userId: user.value.userId,
      },
    )

    if (data.items?.length) {
      termsItems.value = data.items
    }
  } catch (e) {
    console.error(e)
  } finally {
    deepLoader.hide()
  }
}

onFetchAll()

async function onCreate(status: CreateTermsOfUseDeclarationRequestStatus) {
  try {
    await deepAdminBackendAPI.value.termsOfUseDeclarations.create({
      type: TERMS_OF_USE_TYPE,
      orgId: params.value.organizationId,
      status,
      subject: {
        userId: user.value.userId,
      },
    })
    if (status === 'accepted') {
      toast.success(t('deepbox.settings.cloudoffice.toast.activate.success'))
    } else {
      toast.warning(t('deepbox.settings.cloudoffice.toast.deactivate.success'))
    }
    void onFetchAll()
  } catch (e) {
    console.error(e)
  }
}
</script>
